import React, { Suspense, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { LayoutGroup, motion } from 'motion/react';
import useMeasure from 'react-use-measure';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, styles, ContentLoader, Button, Heading, Chip, InlineNotification } from '@frontend/design-system';
import { type WidgetSizes, librarySpace } from '../../constants';
import { WidgetizedDashboardTrackingIds } from '../../tracking-ids';

interface WidgetLibraryItemProps {
  innerRef?: React.LegacyRef<HTMLDivElement>;
  widgets: WidgetPromoConfig[];
  onAddWidget: (id: string, selectedSize: WidgetSizes) => void;
  onDismiss?: () => void;
}

type WidgetPromoConfig = {
  id: string;
  title: string;
  description: string;
  currentSize: WidgetSizes;
  isEnabled?: boolean;
  libraryImage?: React.FC;
};

const MotionText = motion.create(Text);
const MotionButton = motion.create(Button);

const PromoHeader: React.FC<{ onDismiss?: () => void; total: number }> = ({ onDismiss, total }) => {
  const { t } = useTranslation('dashboard');

  return (
    <motion.header
      layout='position'
      css={css`
        margin-bottom: ${theme.spacing(2.75)};
        display: flex;
        align-items: center;
        gap: ${theme.spacing(1)};
      `}
    >
      <NewChip />
      {/*TODO: Using t("Widgets Available", { count: total }) doesn't seem to be working. Investigate the issue */}
      <Heading level={2}>{`${
        total > 1 ? t('{{count}} Widgets Available', { count: total }) : t('Widget Available')
      }`}</Heading>
      <Button
        iconName='x'
        css={css`
          margin-left: auto;
        `}
        size='large'
        onClick={onDismiss}
        variant='secondary'
        trackingId={WidgetizedDashboardTrackingIds.widgetPromoDismiss}
      />
    </motion.header>
  );
};

export const NewChip: React.FC = () => {
  const { t } = useTranslation('dashboard');

  return (
    <span css={newChipWrapperStyle}>
      <Chip
        css={css`
          background: ${theme.colors.secondary.seaweed50};
          color: ${theme.colors.white};
        `}
      >
        {t('New')}
      </Chip>
    </span>
  );
};

const WidgetPreview: React.FC<{
  component: React.FC | null;
  currentSize: WidgetSizes;
}> = ({ component: Component, currentSize }) => {
  return (
    <motion.picture layout='position' css={pictureStyle}>
      <motion.div
        layout
        style={{
          aspectRatio: librarySpace[currentSize]['ratio'],
        }}
        css={imageContainerStyle}
      >
        <Suspense fallback={<ContentLoader show />}>{Component && <Component css={previewImageStyle} />}</Suspense>
      </motion.div>
    </motion.picture>
  );
};

const WidgetInfo: React.FC<{
  title: string;
  description: string;
  activeWidgetIndex: number;
}> = ({ title, description, activeWidgetIndex }) => {
  return (
    <motion.section style={{ overflow: 'hidden' }} layout>
      <motion.header initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} css={headerStyle}>
        <MotionText
          layout='position'
          size='large'
          weight='bold'
          as='h3'
          key={`title-${activeWidgetIndex}`}
          css={styles.truncate}
        >
          {title}
        </MotionText>
      </motion.header>
      <MotionText
        layout='position'
        key={`description-${activeWidgetIndex}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        size='large'
        css={[
          styles.multilineTruncate(4),
          css`
            color: ${theme.colors.neutral70};
          `,
        ]}
      >
        {description}
      </MotionText>
    </motion.section>
  );
};

const AddWidgetAction: React.FC<{
  widget: WidgetPromoConfig;
  onAddWidget: (id: string, selectedSize: WidgetSizes) => void;
}> = ({ widget, onAddWidget }) => {
  const { t } = useTranslation('dashboard');
  const { id, currentSize, isEnabled } = widget;

  if (isEnabled) {
    return (
      <InlineNotification
        status='success'
        size='medium'
        css={css`
          margin-left: auto;
        `}
      >
        {t('Added to Dashboard')}
      </InlineNotification>
    );
  }

  return (
    <MotionButton
      layout='size'
      trackingId={WidgetizedDashboardTrackingIds.addWidgetPromo(id)}
      onClick={() => onAddWidget(id, currentSize)}
      css={css`
        height: 40px;
        margin-left: auto;
      `}
    >
      <Icon name='add' />
      <span
        css={css`
          margin-left: ${theme.spacing(0.5)};
        `}
      >
        {t('Add to Dashboard')}
      </span>
    </MotionButton>
  );
};

const WidgetCarousel: React.FC<{
  total: number;
  handleChangeWidget: (direction: 'next' | 'prev') => void;
  active: number;
}> = ({ total, handleChangeWidget, active }) => {
  return (
    <motion.div layout='position' css={carouselStyle}>
      <Button
        disabled={active === 0}
        iconName='caret-left'
        variant='secondary'
        size='small'
        trackingId={WidgetizedDashboardTrackingIds.widgetPromoPrevious}
        onClick={() => handleChangeWidget('prev')}
      />
      <Text
        size='large'
        color='light'
        css={css`
          white-space: nowrap;
        `}
      >
        {active + 1} / {total}
      </Text>
      <Button
        disabled={active === total - 1}
        iconName='caret-right'
        variant='secondary'
        size='small'
        trackingId={WidgetizedDashboardTrackingIds.widgetPromoNext}
        onClick={() => handleChangeWidget('next')}
      />
    </motion.div>
  );
};

const WidgetControls: React.FC<{
  totalWidgets: number;
  activeWidgetIndex: number;
  currentWidget: WidgetPromoConfig;
  onChangeWidget: (direction: 'next' | 'prev') => void;
  onAddWidget: (id: string, selectedSize: WidgetSizes) => void;
}> = ({ totalWidgets, activeWidgetIndex, currentWidget, onChangeWidget, onAddWidget }) => {
  return (
    <motion.div layout={false} css={controlsStyle}>
      {totalWidgets > 1 && (
        <motion.div>
          <WidgetCarousel total={totalWidgets} handleChangeWidget={onChangeWidget} active={activeWidgetIndex} />
        </motion.div>
      )}
      <motion.div layout={false} className='add-widget-container' css={addWidgetContainerStyle}>
        <AddWidgetAction widget={currentWidget} onAddWidget={onAddWidget} />
      </motion.div>
    </motion.div>
  );
};

export const WidgetPromo: React.FC<WidgetLibraryItemProps> = ({ widgets, innerRef, onAddWidget, onDismiss }) => {
  const [activeWidgetIndex, setActiveWidgetIndex] = React.useState(0);
  const [ref, { height }] = useMeasure();
  const totalWidgets = widgets.length;
  const activeIndex = useMemo(() => {
    return activeWidgetIndex >= totalWidgets ? totalWidgets - 1 : activeWidgetIndex;
  }, [totalWidgets, activeWidgetIndex]);
  const currentWidget = widgets[activeIndex];
  const { id, title, description, currentSize } = currentWidget;
  const Component = widgets[activeIndex]?.libraryImage ?? null;

  const handleChangeWidget = useCallback(
    (direction: 'next' | 'prev') => {
      setActiveWidgetIndex((prevIndex) => {
        if (direction === 'next') {
          return Math.min(prevIndex + 1, totalWidgets - 1);
        } else {
          return Math.max(prevIndex - 1, 0);
        }
      });
    },
    [totalWidgets]
  );

  const handleAddWidget = useCallback(
    (widgetId: string, selectedSize: WidgetSizes) => {
      onAddWidget(widgetId, selectedSize);

      setTimeout(() => {
        const updatedWidgets = widgets.map((widget, index) =>
          index === activeIndex ? { ...widget, isEnabled: true } : widget
        );

        const remainingNotEnabledWidgets = updatedWidgets.filter((widget) => !widget.isEnabled);

        if (remainingNotEnabledWidgets.length === 0) {
          onDismiss?.();
          return;
        }

        let nextIndex = -1;

        for (let i = activeIndex + 1; i < totalWidgets; i++) {
          if (!updatedWidgets[i].isEnabled) {
            nextIndex = i;
            break;
          }
        }

        if (nextIndex === -1) {
          for (let i = activeIndex - 1; i >= 0; i--) {
            if (!updatedWidgets[i].isEnabled) {
              nextIndex = i;
              break;
            }
          }
        }

        if (nextIndex !== -1) {
          setActiveWidgetIndex(nextIndex);
        } else {
          onDismiss?.();
        }
      }, 500);
    },
    [onAddWidget, onDismiss, activeIndex, totalWidgets, widgets]
  );

  return (
    <LayoutGroup>
      <motion.div
        id='widget-promo'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, height: height || 'auto' }}
        exit={{ opacity: 0, y: 20 }}
        transition={{
          opacity: {
            delay: 0.3,
            duration: 0.3,
          },
        }}
        layoutRoot
        style={{
          position: 'absolute',
          overflow: 'auto',
          marginLeft: theme.spacing(1),
          marginRight: theme.spacing(1),
          bottom: theme.spacing(2),
          right: theme.spacing(0),
          zIndex: theme.zIndex.highest,
          maxWidth: `calc(100% - 2 * ${theme.spacing(1)})`,
        }}
        css={promoContainerStyle}
      >
        <motion.div
          css={css`
            padding: ${theme.spacing(3)};
            padding-top: ${theme.spacing(2.75)};
            position: absolute;
            bottom: 0;
            width: 100%;
          `}
          ref={composeRefs(innerRef as any, ref as any)}
        >
          <PromoHeader onDismiss={onDismiss} total={totalWidgets} />

          <motion.div layout id={id} style={{ position: 'relative' }} css={containerStyle}>
            <WidgetPreview component={Component} currentSize={currentSize} />

            <WidgetInfo title={title} description={description} activeWidgetIndex={activeIndex} />

            <WidgetControls
              totalWidgets={totalWidgets}
              activeWidgetIndex={activeIndex}
              currentWidget={currentWidget}
              onChangeWidget={handleChangeWidget}
              onAddWidget={handleAddWidget}
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </LayoutGroup>
  );
};

const promoContainerStyle = css`
  box-shadow: 0px 4px 12px 3px #00215226;
  border-radius: ${theme.borderRadius.large};
  background: ${theme.colors.white};
  z-index: ${theme.zIndex.highest};
  width: 400px;
  overflow: hidden;
`;

const newChipWrapperStyle = css`
  --borderWidth: 2px;
  background: linear-gradient(135deg, #4a9b8e 0%, #2d5a54 100%);
  position: relative;
  border-radius: ${theme.borderRadius.small};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15);

  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: linear-gradient(
        90deg,
        transparent 47%,
        rgba(255, 255, 255, 0.05) 49%,
        rgba(255, 255, 255, 0.05) 51%,
        transparent 53%
      ),
      linear-gradient(
        0deg,
        transparent 47%,
        rgba(255, 255, 255, 0.05) 49%,
        rgba(255, 255, 255, 0.05) 51%,
        transparent 53%
      );
    background-size: 10px 10px;
    border-radius: ${theme.borderRadius.small};
    pointer-events: none;
  }

  ::after {
    content: '';
    position: absolute;
    top: calc(-1 * var(--borderWidth));
    left: calc(-1 * var(--borderWidth));
    height: calc(100% + var(--borderWidth) * 2);
    width: calc(100% + var(--borderWidth) * 2);
    background: linear-gradient(60deg, #2d5a54, #4a9b8e, #5cb3a6, #4ecdc4, #3a8b7f, #2d6b5f, #4a9b8e, #2d5a54);
    border-radius: ${theme.borderRadius.small};
    z-index: -1;
    animation: animatedgradient 3s ease alternate infinite;
    background-size: 300% 300%;
  }

  @keyframes animatedgradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const containerStyle = css`
  border-radius: ${theme.borderRadius.large};
  min-width: 0;
  display: flex;
  flex-direction: column;
  z-index: ${theme.zIndex.highest};
`;

const pictureStyle = css`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing(3)};
  background: ${theme.colors.neutral5};
  display: flex;
  z-index: ${theme.zIndex.low};
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing(1)};
  border-radius: ${theme.borderRadius.medium};
`;

const imageContainerStyle = css`
  box-shadow: 0px 4px 12px 3px #00215226;
  border-radius: ${theme.borderRadius.medium};
  background: ${theme.colors.white};
  height: 100%;
  max-width: 100%;
  position: relative;
  max-height: max-content;
`;

const previewImageStyle = css`
  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  animation: fade-in 0.5s ease-in;
  .responsive-svg {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
  }
`;

const headerStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
  margin-bottom: ${theme.spacing(1.5)};
`;

const controlsStyle = css`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  margin-top: ${theme.spacing(3)};
`;

const carouselStyle = css`
  min-width: 114px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const addWidgetContainerStyle = css`
  width: 100%;
  display: flex;
  justify-content: end;
  padding-bottom: ${theme.spacing(0.5)};
  transition: 0.2s ease;
  margin-left: auto;
`;
